import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

declare const __AUTOMOCK_ENABLED__: boolean;

export type MockBundleData = {
  enable: boolean;
  data: unknown;
  delay?: number;
  status?: number;
  isBinary?: boolean;
};

export type MockInterceptorOptions = {
  mockData: Record<string, MockBundleData>;
  enabled?: boolean | (() => boolean);
  onMockHit?: (url: string, method: string, mock: MockBundleData) => void;
  onBypass?: (url: string, method: string, reason: string) => void;
};

type MockEnabledState = boolean | undefined;

declare global {
  interface Window {
    __MOCK_ENABLED__?: MockEnabledState;
  }
}

class MockInterceptor {
  private options: MockInterceptorOptions;

  constructor(options: MockInterceptorOptions) {
    this.options = options;
  }

  private normalizeUrlPath(url: string, baseURL?: string): string {
    if (!url) return "";

    try {
      const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
      const browserOrigin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "http://localhost";

      if (isAbsoluteUrl) {
        return new URL(url).pathname;
      }

      if (baseURL) {
        const normalizedBase = baseURL.endsWith("/") ? baseURL : `${baseURL}/`;
        const normalizedUrl = url.replace(/^\/+/, "");
        return new URL(normalizedUrl, normalizedBase).pathname;
      }

      return new URL(url, browserOrigin).pathname;
    } catch {
      return url.split(/[?#]/)[0] || "";
    }
  }

  /**
   * Check if mock is enabled
   */
  isEnabled(): boolean {
    // Check runtime flag first
    if (window.__MOCK_ENABLED__ !== undefined) {
      return window.__MOCK_ENABLED__;
    }
    // Check environment variable
    if (typeof this.options.enabled === "function") {
      return this.options.enabled();
    }
    return this.options.enabled ?? false;
  }

  /**
   * Find matching mock data for the request
   * Supports both formats:
   * - "GET /api/v1/asset/xxx" (HTTP method + URL)
   * - "/api/v1/asset/xxx/get.js" (File path format from automock plugin)
   */
  private findMock(url: string, method: string, baseURL?: string): MockBundleData | null {
    const methodUpper = method.toUpperCase();
    const methodLower = method.toLowerCase();
    const normalizedPath = this.normalizeUrlPath(url, baseURL);

    // Try HTTP method + URL format first (for compatibility)
    const httpMethodKey = `${methodUpper} ${url}`;
    if (this.options.mockData[httpMethodKey]) {
      return this.options.mockData[httpMethodKey];
    }

    const normalizedHttpMethodKey = `${methodUpper} ${normalizedPath}`;
    if (this.options.mockData[normalizedHttpMethodKey]) {
      return this.options.mockData[normalizedHttpMethodKey];
    }

    // Try file path format from automock plugin: /api/v1/asset/xxx/get.js
    const filePathKey = `${normalizedPath}/${methodLower}.js`;
    if (this.options.mockData[filePathKey]) {
      return this.options.mockData[filePathKey];
    }

    return null;
  }

  /**
   * Determine if request should be mocked
   */
  shouldMock(url: string, method: string, baseURL?: string): boolean {
    if (!this.isEnabled()) {
      return false;
    }

    const mock = this.findMock(url, method, baseURL);
    return mock !== null && mock.enable;
  }

  /**
   * Get mock data for request
   */
  getMock(url: string, method: string, baseURL?: string): MockBundleData | null {
    if (!this.shouldMock(url, method, baseURL)) {
      return null;
    }
    return this.findMock(url, method, baseURL);
  }

  /**
   * Setup axios interceptor
   */
  setupAxios(axiosInstance: AxiosInstance): void {
    axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const url = config.url || "";
        const method = config.method?.toUpperCase() || "GET";
        const normalizedUrl = this.normalizeUrlPath(url, config.baseURL);

        const mock = this.getMock(url, method, config.baseURL);

        if (mock) {
          // Trigger mock hit callback
          this.options.onMockHit?.(normalizedUrl, method, mock);

          // Set adapter to return mock data
          (config as InternalAxiosRequestConfig & { adapter: () => Promise<AxiosResponse> }).adapter = async () => {
            const { data, delay = 0, status = 200 } = mock;

            // Simulate delay
            if (delay > 0) {
              await new Promise((resolve) => setTimeout(resolve, delay));
            }

            return {
              data,
              status,
              statusText: "OK",
              headers: {},
              config,
            };
          };
        } else {
          // Trigger bypass callback
          const reason = !this.isEnabled()
            ? "Mock disabled globally"
            : "No matching mock found or mock disabled";
          this.options.onBypass?.(normalizedUrl, method, reason);
        }

        return config;
      },
      (error: unknown) => Promise.reject(error),
    );
  }
}

/**
 * Create mock interceptor instance
 */
export function createMockInterceptor(
  options: MockInterceptorOptions,
): MockInterceptor {
  return new MockInterceptor(options);
}

/**
 * Load mock data from bundled JSON file
 */
export async function loadMockData(): Promise<Record<string, MockBundleData>> {
  try {
    // In production, mock-data.json is generated by automock plugin
    // Use absolute path to load it from dist directory
    const response = await fetch("/mock-data.json");
    if (!response.ok) {
      console.warn(
        "[MockInterceptor] Failed to load mock-data.json:",
        response.statusText,
      );
      return {};
    }
    const data = (await response.json()) as Record<string, MockBundleData>;
    return data;
  } catch (error) {
    console.warn("[MockInterceptor] Failed to load mock-data.json:", error);
    return {};
  }
}

async function createInterceptorFromBundle(): Promise<MockInterceptor> {
  const mockData = await loadMockData();
  const isEnabled = typeof __AUTOMOCK_ENABLED__ !== "undefined" && __AUTOMOCK_ENABLED__;

  return new MockInterceptor({
    mockData,
    enabled: isEnabled,
    onMockHit: (url: string, method: string) => {
      console.log(`[MOCK HIT] ${method} ${url}`);
    },
    onBypass: (url: string, method: string, reason: string) => {
      console.log(`[MOCK BYPASS] ${method} ${url} - ${reason}`);
    },
  });
}

export async function initMockInterceptor(
  axiosInstance?: AxiosInstance,
): Promise<void> {
  if (!axiosInstance) {
    throw new Error(
      "[MockInterceptor] axiosInstance is required. Please provide an axios instance.",
    );
  }

  const interceptor = await createInterceptorFromBundle();
  interceptor.setupAxios(axiosInstance);
}

export function setMockEnabled(enabled: boolean): void {
  window.__MOCK_ENABLED__ = enabled;
}

export function isMockEnabled(): boolean {
  return !!window.__MOCK_ENABLED__;
}

type HttpInstance = {
  constructor: { axiosInstance: AxiosInstance };
};

let httpInstanceRef: HttpInstance | undefined;

export function registerHttpInstance(http: HttpInstance): void {
  httpInstanceRef = http;
}

export async function initMockInterceptorForPureHttp(): Promise<void> {
  if (!httpInstanceRef) {
    throw new Error(
      "[MockInterceptor] http instance not registered. Call registerHttpInstance(http) first.",
    );
  }

  const interceptor = await createInterceptorFromBundle();
  interceptor.setupAxios(httpInstanceRef.constructor.axiosInstance);
}
